import { useEffect } from "react";

// material-ui
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputLabel,
Stack,
TextField,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { openSnackbar } from "api/snackbar";
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
// types
import { SnackbarProps } from "types/snackbar";
import { useAppDispatch } from "hooks";
import { PaymentModel } from "model/payment";
import { addPayment, editPayment } from "store/actions/main/payment";
import { FormHelperText } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { FormControl } from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { Switch } from "@mui/material";
import _ from "lodash";

// constant
const getInitialValues = (customer: PaymentModel | null) => {
  const newCustomer = {
    name: "",
    name_trans: "",
    status: false,
  };
  if (customer) {
    return _.merge({}, newCustomer, customer);
  }

  return newCustomer;
};

// ==============================|| PAYMENT ADD / EDIT - FORM ||============================== //

const FormPaymentAdd = ({
  customer,
  id,
  closeModal,
  fetchData,
}: {
  fetchData: any;

  id: number;
  customer: PaymentModel | null;
  closeModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required("English name is required"),
    name_trans: Yup.string().required("Arabic name is required"),
  });
  useEffect(() => {
    fetchData();

    // Format the start and end dates if they are provided as Dayjs objects
  }, [dispatch]);

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (customer) {
          // Dispatch editCategories action if customer exists
          await dispatch(
            editPayment({
              id: customer.id || 0,
              data: {
                name: values.name,
                name_trans: values.name_trans,
                status: values.status
              },
            }),
          ).then((action) => {
            fetchData();
            if (action.type === "payment/editPayment/fulfilled") {
              openSnackbar({
                open: true,
                message: "Payment edited successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            } else {
              openSnackbar({
                open: true,
                message: "some thing went wrong",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "error",
                },
              } as SnackbarProps);
              closeModal();
            }
          });
        } else {
          // Dispatch create newCat action if no customer exists
          await dispatch(
            addPayment({
              name: values.name,
              name_trans: values.name_trans,
              status: values.status
            }),
          ).then((action) => {
            fetchData();
            if (action.type === "payment/addPayment/fulfilled") {
              openSnackbar({
                open: true,
                message: "Payment added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            } else {
              openSnackbar({
                open: true,
                message: "something went wrong",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "error",
                },
              } as SnackbarProps);
              closeModal();
            }
          });
        }

        closeModal();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;



  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle style={{textAlign:'center', fontSize:'22px', fontWeight:'bold', color:'rgb(22, 119, 255)', marginBottom:'10px', }}>
              {customer ? "Edit payment details" : "Add new payment"}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name"> <FormattedMessage id="English Name" /><span style={{color:'red'}}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      placeholder={intl.formatMessage({ id: "Enter english name", defaultMessage: "Enter english name" })}
                      {...getFieldProps("name")}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1}/>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name"><FormattedMessage id="Arabic Name" /><span style={{color:'red'}}>*</span>
                    </InputLabel>
                    <TextField
                    fullWidth
                      id="customer-name"
                      name="name_trans"
                      {...getFieldProps("name_trans")}
                      placeholder={intl.formatMessage({ id: "Enter arabic name", defaultMessage: "Enter arabic name" })}
                      error={Boolean(touched.name_trans && errors.name_trans)}
                      helperText={touched.name_trans && errors.name_trans}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} md={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-status">
                      <FormattedMessage id="Status" />
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="customer-status"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={formik.values.status ? <FormattedMessage id="active" /> : <FormattedMessage id="inactive" />}
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-login"
                        sx={{ pl: 1.75 }}
                      >
                        {errors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                  <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                    <Grid item md={2}/>
                    <Grid item md={4}>
                      <Button fullWidth type="submit" variant="contained">
                        {isSubmitting? <CircularProgress size="1.5rem" style={{color:'white'}}/> : <FormattedMessage id="Submit" />}
                      </Button>
                    </Grid>
                    <Grid item md={4}>
                      <Button  fullWidth color="secondary" variant="outlined" onClick={closeModal}>
                        <FormattedMessage id="Cancel" />
                      </Button>
                    </Grid>
                    <Grid item md={2}/>
                  </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default FormPaymentAdd;
